// Documents vault (plan workstream 1, Phase 2): every document on the client record,
// with a retention rule of 7 years from the end of the relationship. Pure.

export const DOC_CATEGORIES = [
  'fact_find', 'risk_profile', 'soa', 'roa', 'review_pack', 'fds', 'fee_consent', 'statement', 'tax', 'estate', 'insurance', 'correspondence', 'other',
] as const;
export type DocCategory = (typeof DOC_CATEGORIES)[number];

export const DOC_CATEGORY_LABEL: Record<DocCategory, string> = {
  fact_find: 'Fact find',
  risk_profile: 'Risk profile',
  soa: 'Statement of Advice',
  roa: 'Record of Advice',
  review_pack: 'Review pack',
  fds: 'Fee disclosure statement',
  fee_consent: 'Fee consent',
  statement: 'Platform or bank statement',
  tax: 'Tax',
  estate: 'Estate planning',
  insurance: 'Insurance',
  correspondence: 'Correspondence',
  other: 'Other',
};

const BRISBANE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane' });
/** Brisbane calendar day of a timestamp (archive time is stored in UTC). */
export const brisbaneDay = (d: Date | string): string => BRISBANE.format(new Date(d));

/** Records are kept 7 years after the relationship ends (Corporations Act record keeping). */
export const RETENTION_YEARS = 7;

/** File types the vault accepts, by extension. Executables and archives are refused. */
export const ALLOWED_EXTENSIONS: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  txt: 'text/plain',
  msg: 'application/vnd.ms-outlook',
  eml: 'message/rfc822',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
};

export const MAX_DOC_BYTES = 20 * 1024 * 1024;

export function extensionOf(name: string): string {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(name.trim());
  return m ? m[1].toLowerCase() : '';
}

/** Content type for an accepted file name, or null when the vault refuses it. */
export function contentTypeFor(name: string): string | null {
  return ALLOWED_EXTENSIONS[extensionOf(name)] ?? null;
}

/**
 * A storage-safe file name: path parts dropped, anything outside letters, digits,
 * dot, dash and underscore replaced, length capped, extension kept.
 */
export function safeFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? 'file';
  const ext = extensionOf(base);
  const stem = (ext ? base.slice(0, -(ext.length + 1)) : base)
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^[-.]+|[-.]+$/g, '').slice(0, 80) || 'file';
  return ext ? `${stem}.${ext}` : stem;
}

/** Title from a file name when the adviser gives none: extension off, separators to spaces. */
export function titleFromFileName(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? name;
  const ext = extensionOf(base);
  const stem = ext ? base.slice(0, -(ext.length + 1)) : base;
  return stem.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 200) || 'Document';
}

function addYears(iso: string, years: number): string {
  const y = Number(iso.slice(0, 4)) + years;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  // 29 February lands on 28 February in a non-leap year.
  return iso.slice(5, 10) === '02-29' && !leap ? `${y}-02-28` : `${y}${iso.slice(4, 10)}`;
}

/**
 * The date until which a client's documents must be kept: null while the client is
 * active (the relationship has not ended), else 7 years after it ended (archived).
 */
export function retentionUntil(relationshipEndedOn: string | null): string | null {
  return relationshipEndedOn ? addYears(relationshipEndedOn.slice(0, 10), RETENTION_YEARS) : null;
}

/** True when documents still have to be kept on `today`. */
export function underRetention(relationshipEndedOn: string | null, today: string): boolean {
  const until = retentionUntil(relationshipEndedOn);
  return until === null || today < until;
}
